import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, negocio, type Pagina } from '../comun';
import type { IdNegocio } from '../operaciones';
import type { components, operations } from '../tipos/clientes';
import type { components as Entregas, operations as OperacionesEntregas } from '../tipos/entregas';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];
type Respuesta<O extends keyof operations, C extends keyof operations[O]['responses']> = operations[O]['responses'][C] extends { content: { 'application/json': infer T } } ? T : never;

export type Cliente = Esquema<'Customer'>;
export type ResumenCliente = Esquema<'CustomerSummary'>;
export type ListaClientes = Esquema<'CustomerSummaryList'>;
/**
 * `CustomerCreate`: el generador lo junta con `unknown` por su `anyOf` (nombre, teléfono o email); aquí, sus propiedades
 * tal cual del contrato.
 */
export interface NuevoCliente {
    display_name?: string;
    phone?: string;
    email?: string | null;
    instagram_username?: string | null;
    locale?: string;
    source: 'panel' | 'tablet' | 'api';
    tag_ids?: string[];
    field_values?: Record<string, unknown>;
    consents?: Esquema<'ConsentInput'>[];
    birth_date?: string | null;
    birth_date_consent?: boolean;
}
export type ResultadoAlta = Esquema<'CustomerCreateResult'>;
export type CambiosCliente = Esquema<'CustomerUpdate'>;
export type EstadoFicha = Esquema<'ProfileStatus'>;
export type Canal = Esquema<'Channel'>;
export type Finalidad = Esquema<'Purpose'>;
export type EstadoPermiso = Esquema<'ConsentState'>;
export type OrigenPermiso = Esquema<'ConsentSource'>;
export type OrigenFicha = Esquema<'ProfileSource'>;
export type PermisoDado = Esquema<'ConsentInput'>;
export type RegistroPermiso = Esquema<'ConsentRecord'>;
export type PermisosCliente = Esquema<'ConsentOverview'>;
export type Etiqueta = Esquema<'Tag'>;
export type ListaEtiquetas = Esquema<'TagList'>;
export type CampoPropio = Esquema<'CustomField'>;
export type LineaHistorial = Esquema<'TimelineEntry'>;
export type ListaHistorial = Esquema<'TimelineEntryList'>;
export type Nota = Esquema<'Note'>;
export type ListaNotas = Esquema<'NoteList'>;
export type NuevaNota = Esquema<'NoteCreate'>;
export type Propuesta = Esquema<'CorrectionProposal'>;
export type NuevaPropuesta = Esquema<'CorrectionProposalCreate'>;
export type ListaPropuestas = Esquema<'CorrectionProposalList'>;
export type EvaluacionPuerta = Esquema<'DoorAssessment'>;
export type MotivoPuerta = Esquema<'DoorReasonCode'>;
export type ListaEvaluaciones = Esquema<'DoorAssessmentList'>;
export type NuevaEvaluacion = Esquema<'DoorAssessmentCreate'>;
export type MotivoEvaluacion = Esquema<'DoorReasonCode'>;
export type BloqueoLegal = Esquema<'LegalHold'>;
export type Importe = Esquema<'Money'>;
export type OrdenClientes = NonNullable<NonNullable<operations['listCustomers']['parameters']['query']>['sort']>;
export type ListaCampos = Respuesta<'listCustomFields', 200>;

export type Entrega = Entregas['schemas']['Delivery'];
export type ListaEntregas = Entregas['schemas']['DeliveryList'];
export type CanalEntrega = Entregas['schemas']['DeliveryChannel'];
export type EstadoEntrega = Entregas['schemas']['DeliveryStatus'];
type ConsultaEntregas = NonNullable<OperacionesEntregas['listDeliveries']['parameters']['query']>;

/** Filtros de la lista de clientes (2.1): búsqueda, etiqueta, cuenta, orden y campos propios (G142). */
export interface FiltrosClientes extends Pagina {
    q?: string;
    phone?: string;
    email?: string;
    tag_id?: string;
    status?: 'local' | 'linked';
    sort?: OrdenClientes;
    /** `field[<clave>]=valor`: solo campos de una opción, sí o no y fecha (G142). */
    campos?: Readonly<Record<string, string>>;
}

const ruta = (businessId: IdNegocio, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = (valor: string) => encodeURIComponent(valor);

function parametros(valores: Readonly<Record<string, string | number | boolean | null | undefined>>, extra: [string, string][] = []): string {
    const p = new URLSearchParams();
    for (const [clave, valor] of Object.entries(valores)) {
        if (valor !== undefined && valor !== null && valor !== '') {
            p.set(clave, String(valor));
        }
    }
    for (const [clave, valor] of extra) {
        p.append(clave, valor);
    }
    const texto = p.toString();
    return texto === '' ? '' : `?${texto}`;
}

/**
 * Operaciones de la lista, el alta y la ficha de cliente (2.1 a 2.4), con su `operationId` y los tipos generados de
 * `clientes.yaml`. La ficha se lee con su `ETag` y se cambia con `If-Match` (412 si alguien la cambió antes). La pestaña
 * «Entregas» usa `listDeliveries` y `resendDelivery` de Entregas, que puede no existir todavía (404: sin entregas).
 */
export function operacionesClientes(api: ClienteApi) {
    return {
        // ---- Lista y alta (2.1, 2.2) ----
        /** listCustomers (`clientes.fichas.ver`, «lo suyo» por rol): por cursor, sin total (G121, G140). */
        listCustomers: (businessId: IdNegocio, filtros: FiltrosClientes = {}) => {
            const { campos = {}, ...resto } = filtros;
            const extra = Object.entries(campos).filter(([, v]) => v !== '').map(([clave, v]): [string, string] => [`field[${clave}]`, v]);
            return api.pedir<ListaClientes>('GET', ruta(businessId, `/customers${parametros({ ...resto }, extra)}`));
        },
        /** createCustomer (`clientes.fichas.crear`): 201 creado o 200 `matched_existing` («Ya existía; abrimos su ficha»). */
        createCustomer: (businessId: IdNegocio, datos: NuevoCliente, clave = claveIdempotencia()) =>
            api.pedir<ResultadoAlta>('POST', ruta(businessId, '/customers'), { cuerpo: datos, idempotencia: clave }),
        /** listTags (`clientes.fichas.ver`): etiquetas del negocio (chips neutros en la lista, G143); el catálogo (2.8) pide también las archivadas. */
        listTags: (businessId: IdNegocio, filtros: Pagina & { include_archived?: boolean } = {}) =>
            api.pedir<ListaEtiquetas>('GET', ruta(businessId, `/tags${parametros({ ...filtros })}`)),
        /** listCustomFields (`clientes.fichas.ver`): campos propios, en orden, con `has_values` (G152); el catálogo (2.8) pide también los archivados. */
        listCustomFields: (businessId: IdNegocio, filtros: { include_archived?: boolean } = {}) =>
            api.pedir<ListaCampos>('GET', ruta(businessId, `/custom-fields${parametros({ ...filtros })}`)),

        // ---- Ficha (2.3) ----
        /** getCustomer (`clientes.fichas.ver`): la ficha con su `ETag`. */
        getCustomer: (businessId: IdNegocio, customerId: string): Promise<ConVersion<Cliente>> => api.pedirConVersion<Cliente>('GET', ruta(businessId, `/customers/${id(customerId)}`)),
        /** updateCustomer (`clientes.fichas.editar`): solo datos del negocio, con `If-Match`; 409 si un contacto es de otra ficha. */
        updateCustomer: (businessId: IdNegocio, customerId: string, cambios: CambiosCliente, version: string, clave = claveIdempotencia()) =>
            api.pedirConVersion<Cliente>('PATCH', ruta(businessId, `/customers/${id(customerId)}`), { cuerpo: cambios, siCoincide: version, idempotencia: clave }),
        /** addCustomerTag (`clientes.fichas.editar`). */
        addCustomerTag: (businessId: IdNegocio, customerId: string, tagId: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', ruta(businessId, `/customers/${id(customerId)}/tags`), { cuerpo: { tag_id: tagId }, idempotencia: clave }),
        /** removeCustomerTag (`clientes.fichas.editar`). */
        removeCustomerTag: (businessId: IdNegocio, customerId: string, tagId: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('DELETE', ruta(businessId, `/customers/${id(customerId)}/tags/${id(tagId)}`), { idempotencia: clave }),
        /** listCustomerTimeline (`clientes.fichas.ver`): de lo más reciente a lo más antiguo; `module` filtra. */
        listCustomerTimeline: (businessId: IdNegocio, customerId: string, consulta: Pagina & { module?: string } = {}) =>
            api.pedir<ListaHistorial>('GET', ruta(businessId, `/customers/${id(customerId)}/timeline${parametros({ ...consulta })}`)),
        /** listCustomerConsents (`clientes.fichas.ver`): estado por canal y registro, que solo crece. */
        listCustomerConsents: (businessId: IdNegocio, customerId: string, pagina: Pagina = {}) =>
            api.pedir<PermisosCliente>('GET', ruta(businessId, `/customers/${id(customerId)}/consents${parametros({ ...pagina })}`)),
        /** recordCustomerConsent (`clientes.consentimientos.registrar`, 2.4): avisa al cliente, sin «Deshacer» (G159, G162). */
        recordCustomerConsent: (businessId: IdNegocio, customerId: string, permiso: PermisoDado, clave = claveIdempotencia()) =>
            api.pedir<RegistroPermiso>('POST', ruta(businessId, `/customers/${id(customerId)}/consents`), { cuerpo: permiso, idempotencia: clave }),
        /** listCustomerNotes (`clientes.fichas.ver`; el profesional, solo las de sus citas). */
        listCustomerNotes: (businessId: IdNegocio, customerId: string, consulta: Pagina & { include_archived?: boolean } = {}) =>
            api.pedir<ListaNotas>('GET', ruta(businessId, `/customers/${id(customerId)}/notes${parametros({ ...consulta })}`)),
        /** createCustomerNote (`clientes.notas.escribir`). */
        createCustomerNote: (businessId: IdNegocio, customerId: string, nota: NuevaNota, clave = claveIdempotencia()) =>
            api.pedir<Nota>('POST', ruta(businessId, `/customers/${id(customerId)}/notes`), { cuerpo: nota, idempotencia: clave }),
        /** archiveCustomerNote (`clientes.notas.escribir`): no se borran, se archivan. */
        archiveCustomerNote: (businessId: IdNegocio, customerId: string, noteId: string, clave = claveIdempotencia()) =>
            api.pedir<Nota>('POST', ruta(businessId, `/customers/${id(customerId)}/notes/${id(noteId)}/archive`), { idempotencia: clave }),
        /** listCorrectionProposals (`clientes.fichas.ver`). */
        listCorrectionProposals: (businessId: IdNegocio, customerId: string, pagina: Pagina = {}) =>
            api.pedir<ListaPropuestas>('GET', ruta(businessId, `/customers/${id(customerId)}/correction-proposals${parametros({ ...pagina })}`)),
        /** createCorrectionProposal (`clientes.correcciones.proponer`): caduca a los 30 días (G164). */
        createCorrectionProposal: (businessId: IdNegocio, customerId: string, propuesta: NuevaPropuesta, clave = claveIdempotencia()) =>
            api.pedir<Propuesta>('POST', ruta(businessId, `/customers/${id(customerId)}/correction-proposals`), { cuerpo: propuesta, idempotencia: clave }),
        /** listDoorAssessments (`clientes.fichas.ver`): con `limit=1`, la vigente para la fila «Puerta» (G146, G193). */
        listDoorAssessments: (businessId: IdNegocio, customerId: string, pagina: Pagina = {}) =>
            api.pedir<ListaEvaluaciones>('GET', ruta(businessId, `/customers/${id(customerId)}/door-assessments${parametros({ ...pagina })}`)),
        /**
         * createDoorAssessment (G193): verde (quita la vigente), ámbar o rojo con motivo de la lista cerrada y nota; desde la
         * puerta, con `pass_ref` (el `customer_id` sale de `getPass`).
         */
        createDoorAssessment: (businessId: IdNegocio, customerId: string, datos: NuevaEvaluacion, clave = claveIdempotencia()) =>
            api.pedir<EvaluacionPuerta | undefined>('POST', ruta(businessId, `/customers/${id(customerId)}/door-assessments`), { cuerpo: datos, idempotencia: clave }),
        /** placeCustomerLegalHold (`clientes.rgpd.atender`): «Bloqueada por obligación legal», con motivo (G196). */
        placeCustomerLegalHold: (businessId: IdNegocio, customerId: string, motivo: string, clave = claveIdempotencia()) =>
            api.pedir<BloqueoLegal>('PUT', ruta(businessId, `/customers/${id(customerId)}/legal-hold`), { cuerpo: { reason: motivo }, idempotencia: clave }),
        /** releaseCustomerLegalHold (`clientes.rgpd.atender`): quitar la marca, con motivo (G196). */
        releaseCustomerLegalHold: (businessId: IdNegocio, customerId: string, motivo: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('DELETE', ruta(businessId, `/customers/${id(customerId)}/legal-hold`), { cuerpo: { reason: motivo }, idempotencia: clave }),

        // ---- Entregas de la ficha (2.3, `entregas.yaml`) ----
        /** listDeliveries (`entregas.registro.ver`): las del cliente. Mientras Entregas no exista, 404. */
        listDeliveries: (businessId: IdNegocio, consulta: ConsultaEntregas = {}) =>
            api.pedir<ListaEntregas>('GET', ruta(businessId, `/deliveries${parametros({ ...consulta })}`)),
        /** resendDelivery (`entregas.entregas.reenviar`): por el canal elegido; avisa al cliente, sin «Deshacer» (G159). */
        resendDelivery: (businessId: IdNegocio, deliveryId: string, canal: CanalEntrega | null, clave = claveIdempotencia()) =>
            api.pedir<Entrega>('POST', ruta(businessId, `/deliveries/${id(deliveryId)}/resend`), { cuerpo: canal === null ? {} : { channel: canal }, idempotencia: clave }),
    };
}
